import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Atomic from "../../../../AComponent/Atomic";
import { format } from "date-fns";
import { complexQuery, getUserId } from "../../../../Utils/Common/Library";
import { DeleteOutline, EditOutlined } from "@mui/icons-material";
import InlineReadMore from "../Phases/Assessment/ReadMoreComment";

const { Input } = Atomic;

const CommentInput = ({ onAdd, onBlur, defaultValue }) => {
  const [value, setValue] = useState(defaultValue || "");

  useEffect(() => {
    return () => {
      setValue("");
    };
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Input
        label={"Comments"}
        value={value}
        styles={{ margin: 0, width: "100%" }}
        type={"text"}
        multiline
        minRows={1}
        maxRows={5}
        onChange={(value) => setValue(value)}
        onSend={
          onAdd
            ? () => {
                onAdd(value);
                setValue("");
              }
            : undefined
        }
        onBlur={
          onBlur
            ? () => {
                onBlur(value);
              }
            : undefined
        }
        autoFocus={onBlur ? true : false}
      />
    </Box>
  );
};

const CommentCard = ({
  commentUser,
  commentRole,
  commentTime,
  comment,
  onEdit,
  onDelete,
  blur,
}) => {
  const [name, setName] = useState("");
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    complexQuery({
      RequiredFields: [{ UserID: commentUser }],
      QueryName: "FindUserAndItsOrganisationDetails",
    }).then((res) => {
      setName(res[0]?.FullName);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Paper sx={{ padding: 0, opacity: blur ? "0.5" : undefined }} elevation={0}>
      <Stack rowGap={"0.3rem"} width={"100%"} position={"relative"}>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4rem" }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}>
            <Typography variant="form_h1" color={"primary.light"}>
              {name}
            </Typography>
            {commentRole && (
              <Typography variant="form_h1" color={"primary.light"}>
                ({commentRole})
              </Typography>
            )}
          </Box>
          {!edit && (
            <Box
              sx={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}
            >
              {onEdit && (
                <IconButton
                  onClick={() => {
                    setEdit(true);
                  }}
                  size="small"
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={() => {
                    onDelete();
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>

        {commentTime && (
          <Typography variant="form_text3" sx={{ justifySelf: "flex-end" }}>
            {format(commentTime || Date.now(), "dd-MMM-yyyy, hh:mm aa")}
          </Typography>
        )}
        {edit ? (
          <CommentInput
            defaultValue={comment}
            onBlur={(newComment) => {
              onEdit(newComment);
              setEdit(false);
            }}
          />
        ) : (
          <InlineReadMore text={comment} charLimit={60} />
        )}
      </Stack>
    </Paper>
  );
};

const Comments = ({
  comments,
  onChange,
  role,
  stepId,
  assessmentCreationTimestamp,
}) => {
  const [state, setState] = useState(comments);
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [state.length]); // runs when a new comment is added

  return (
    <Stack rowGap="1.5rem" alignItems={"flex-start"} sx={{ width: "30rem" }}>
      <Typography variant="h2" color={"primary.light"}>
        Comments
      </Typography>
      <Stack
        ref={scrollRef}
        rowGap={"0.8rem"}
        maxHeight={"20rem"}
        className="scroll"
        padding={"0.5rem"}
        width={"100%"}
        sx={{ overflowY: "auto" }} // 🔁 key line
      >
        {state.map((c, index) => {
          const { Comment, CommenterRole, CommentTimestamp, Commenter } = c;

          return (
            <CommentCard
              key={`Comment-${index}`}
              comment={Comment}
              commentRole={CommenterRole}
              commentTime={CommentTimestamp}
              commentUser={Commenter}
              blur={
                !assessmentCreationTimestamp
                  ? false
                  : CommentTimestamp > parseInt(assessmentCreationTimestamp)
              }
              onEdit={
                Commenter === getUserId()
                  ? (comment) => {
                      setState((prevState) => {
                        const newComments = [...prevState];
                        newComments[index].Comment = comment;
                        newComments[index].CommentTimestamp = Date.now();
                        newComments.sort(
                          (a, b) => a.CommentTimestamp - b.CommentTimestamp
                        );
                        onChange(newComments);
                        return newComments;
                      });
                    }
                  : undefined
              }
              onDelete={
                Commenter === getUserId()
                  ? () => {
                      setState((prevState) => {
                        const newComments = [...prevState];
                        newComments.splice(index, 1);
                        newComments.sort(
                          (a, b) => a.CommentTimestamp - b.CommentTimestamp
                        );
                        onChange(newComments);
                        return newComments;
                      });
                    }
                  : undefined
              }
            />
          );
        })}
      </Stack>
      <CommentInput
        onAdd={(comment) => {
          if ((comment || "").trim()) {
            setState((prevState) => {
              const newState = [
                ...prevState,
                {
                  Comment: comment,
                  CommentType: "AI",
                  Step: stepId,
                  Commenter: getUserId(),
                  CommentTimestamp: Date.now(),
                  CommenterRole: role || "",
                },
              ];
              onChange(newState);
              return newState;
            });
          }
        }}
      />
    </Stack>
  );
};

export default Comments;
